/**
 * TelegramSetupPage — Telegram bot ulash sahifasi (spec bo'lim 3)
 *
 * Birinchi marta kirganda yoki Telegram ulanmagan bo'lsa ochiladi.
 * "Keyinroq" tugmasi orqali skip qilish mumkin (majburiy emas).
 */

import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, LogOut, Send } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useLogout } from '@/features/auth/auth.hooks';
import { apiClient, ApiClientError } from '@/shared/lib/api-client';
import { cn } from '@/shared/lib/cn';
import { toast } from '@/shared/ui/toast';

const DEFAULT_BOT_USERNAME = '@avtofix_support';

interface TelegramCheckResponse {
  connected: boolean;
}

export function TelegramSetupPage(): React.ReactElement {
  const navigate = useNavigate();
  const logout = useLogout();
  const [status, setStatus] = useState<'idle' | 'checking' | 'connected'>('idle');

  const botUsername = DEFAULT_BOT_USERNAME;
  const botLink = `https://t.me/${botUsername.replace('@', '')}`;

  const checkMutation = useMutation({
    mutationFn: async (): Promise<TelegramCheckResponse> => {
      return apiClient.get<TelegramCheckResponse>('/telegram/check');
    },
    onSuccess: (data) => {
      if (data.connected) {
        setStatus('connected');
        toast.success('Muvaffaqiyatli ulandi!');
        setTimeout(() => {
          void navigate('/kassa', { replace: true });
        }, 1000);
      } else {
        setStatus('idle');
        toast.error(
          "Bot bilan bog'lanmagan. /start xabarini yuborganingizga ishonch hosil qiling.",
        );
      }
    },
    onError: (err) => {
      setStatus('idle');
      const message =
        err instanceof ApiClientError
          ? err.message
          : "Telegram serveriga ulanib bo'lmadi. Internetni tekshiring.";
      toast.error(message);
    },
  });

  const handleCheck = (): void => {
    setStatus('checking');
    checkMutation.mutate();
  };

  const handleSkip = (): void => {
    void navigate('/kassa', { replace: true });
  };

  const handleLogout = (): void => {
    logout();
    void navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top nav */}
      <header className="flex items-center justify-end gap-2 border-b border-border p-4">
        <button
          type="button"
          onClick={handleSkip}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
        >
          Keyinroq
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-2 rounded-lg border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
        >
          <LogOut className="size-4" aria-hidden />
          Chiqish
        </button>
      </header>

      {/* Main */}
      <main className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          {/* Icon + title */}
          <div className="text-center">
            <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-info/15">
              <Send className="size-8 text-info" aria-hidden />
            </div>
            <h1 className="mt-4 text-2xl font-bold">Telegram Botga ulanish</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Bildirishnomalarni olish uchun bot bilan bog&apos;laning
            </p>
          </div>

          {/* 4 bosqich */}
          <ol className="space-y-3 rounded-2xl border border-border bg-card p-5">
            {[
              'Pastdagi ko\u2019k tugmani bosing',
              "Telegram ilovasi ochiladi va botga o'tasiz",
              'Botga /start xabarini yuboring',
              'Bu sahifaga qaytib, Tekshirish tugmasini bosing',
            ].map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-info/15 text-sm font-semibold text-info">
                  {index + 1}
                </span>
                <span className="pt-0.5 text-sm">{step}</span>
              </li>
            ))}
          </ol>

          {/* Bot'ni ochish */}
          <a
            href={botLink}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'flex h-12 w-full items-center justify-center gap-2 rounded-lg',
              'bg-gradient-to-r from-info to-info/80 text-sm font-semibold text-white',
              'transition-opacity hover:opacity-90',
            )}
          >
            <Send className="size-4" aria-hidden />
            Telegram Botni Ochish
          </a>

          {/* Tekshirish */}
          <button
            type="button"
            onClick={handleCheck}
            disabled={status !== 'idle'}
            className={cn(
              'flex h-11 w-full items-center justify-center gap-2 rounded-lg',
              'border border-border bg-transparent text-sm font-medium',
              'transition-colors hover:bg-muted disabled:opacity-50',
            )}
          >
            {status === 'checking' && (
              <>
                <span className="size-2 animate-pulse rounded-full bg-info" />
                Tekshirilmoqda...
              </>
            )}
            {status === 'connected' && (
              <>
                <CheckCircle2 className="size-4 text-success" aria-hidden />
                Ulandi!
              </>
            )}
            {status === 'idle' && 'Tekshirish'}
          </button>
        </div>
      </main>
    </div>
  );
}
